import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Image as ImageIcon,
  Italic,
  MousePointerClick,
  Omega,
  Shapes,
  Strikethrough,
  Type,
  Underline,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { withElementDefaults, withTextDefaults } from '../../utils/invitationStyle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { COLOR_SWATCHES, FOCUS_RING, TEXT_INPUT } from './constants';
import { Collapsible, ColorField, Segmented, SliderRow, Stepper, SwitchRow } from './controls';
import { elementLabel } from './designUtils';
import { FontPicker } from './FontPicker';

const LABEL = 'text-[13px] font-bold text-ink';
const HIGHLIGHT_SWATCHES = [
  ['Pink marker', '#F8C8E0'],
  ['Yellow marker', '#FCE38A'],
  ['Blue marker', '#BDEBFA'],
  ['Green marker', '#CDEBC5'],
];
const LABEL_SWATCHES = [
  ['Yellow', '#FFC21A'],
  ['Cream', '#F2F0EB'],
  ['Ink', '#1A1A1A'],
  ['Pink', '#EC008C'],
];

const PanelHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex gap-2.5 items-center">
    <span className="h-[34px] w-[34px] rounded-[10px] bg-cream flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
    <div className="flex flex-col min-w-0">
      <span className="text-[15px] font-extrabold text-ink">{title}</span>
      {subtitle ? <span className="text-xs text-[#6B665C] truncate">{subtitle}</span> : null}
    </div>
  </div>
);

/** Font picker; every option is previewed in its own font. */
/** Font control: the preview picker (FontPicker.js). */
export const FontSelect = ({ value, fonts, onChange, sampleText }) => (
  <FontPicker value={value} fonts={fonts} onChange={onChange} sampleText={sampleText} />
);

const STYLE_BUTTONS = [
  ['font_weight', 'bold', 'normal', 'Bold', Bold],
  ['font_style', 'italic', 'normal', 'Italic', Italic],
  ['underline', true, false, 'Underline', Underline],
  ['strike', true, false, 'Strikethrough', Strikethrough],
];

/**
 * Properties of a text field. `section` limits the controls shown (used by the
 * phone quick controls): all | text | font | size | style | color | align | effects | glyphs.
 * `glyphs` is the glyph browser for this text box (only shown here, in the text tools).
 */
export const TextProperties = ({ field: raw, fonts, onPatch, onCaret, glyphs, section = 'all' }) => {
  const f = withTextDefaults(raw);
  const set = (prop, value) => onPatch({ [prop]: value }, `${f.id}-${prop}`);
  const show = (s) => section === 'all' || section === s;

  return (
    <div className="flex flex-col gap-3.5" data-testid="text-properties">
      {section === 'all' && <PanelHeader icon={Type} title="Text" subtitle={f.label || 'Custom text'} />}

      {show('text') && (
        <textarea
          dir="auto"
          aria-label="Text"
          value={f.text}
          maxLength={2000}
          rows={Math.min(5, Math.max(2, (f.text.match(/\n/g) || []).length + 1))}
          onChange={(e) => set('text', e.target.value)}
          onSelect={(e) => onCaret?.(f.id, e.target.selectionStart, e.target.selectionEnd)}
          className="rounded-xl px-3 py-2.5 font-hebrew text-[17px] leading-snug resize-y gradient-border-strong outline-none focus-visible:ring-[3px] focus-visible:ring-pink/10"
          data-testid="field-text-input"
        />
      )}

      {show('font') && (
        <>
          <FontSelect value={f.font_family} fonts={fonts} onChange={(v) => set('font_family', v)} sampleText={f.text} />
          {section !== 'all' && (
            <Segmented
              label="Weight"
              options={[['normal', 'Regular'], ['bold', 'Bold']]}
              value={f.font_weight}
              onChange={(v) => set('font_weight', v)}
            />
          )}
        </>
      )}

      {section === 'all' && (
        <div className="grid grid-cols-2 gap-2">
          <Select value={f.font_weight} onValueChange={(v) => set('font_weight', v)}>
            <SelectTrigger aria-label="Weight" className="h-10 rounded-[10px] bg-white border-[#DCD6C8] px-3 text-sm font-bold shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="normal">Regular</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
            </SelectContent>
          </Select>
          <Stepper label="Font size" value={f.font_size} min={4} max={400} step={2} onChange={(v) => set('font_size', v)} />
        </div>
      )}
      {section === 'size' && (
        <>
          <Stepper label="Font size" value={f.font_size} min={4} max={400} step={2} onChange={(v) => set('font_size', v)} />
          <SliderRow label="Size" value={f.font_size} min={8} max={160} onChange={(v) => set('font_size', v)} format={(v) => `${v}px`} />
        </>
      )}

      {(show('style') || show('color')) && (
        <div className={cn('flex gap-1.5 items-center', section === 'all' ? '' : 'flex-col items-stretch gap-3')}>
          {show('style') && (
            <div className="flex gap-1.5" role="group" aria-label="Text style">
              {STYLE_BUTTONS.map(([prop, on, off, name, Icon]) => {
                const active = f[prop] === on;
                return (
                  <button
                    key={prop}
                    type="button"
                    aria-label={name}
                    aria-pressed={active}
                    title={name}
                    onClick={() => onPatch({ [prop]: active ? off : on }, null)}
                    className={cn(
                      'h-10 w-10 rounded-[10px] flex items-center justify-center border',
                      active ? 'bg-ink text-paper border-ink' : 'bg-white text-ink border-[#DCD6C8] hover:bg-cream',
                      FOCUS_RING
                    )}
                    data-testid={`style-${prop}`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {show('color') && (
        <ColorField label="Colour" value={f.color} swatches={COLOR_SWATCHES} onChange={(v) => set('color', v)} />
      )}

      {show('align') && (
        <>
          <Segmented
            label="Alignment"
            options={[
              ['right', 'Align right', AlignRight],
              ['center', 'Align centre', AlignCenter],
              ['left', 'Align left', AlignLeft],
            ]}
            value={f.text_align}
            onChange={(v) => set('text_align', v)}
          />
          <div className="flex items-center gap-2.5">
            <span className={cn(LABEL, 'w-16')}>Direction</span>
            <Segmented
              label="Text direction"
              className="flex-1"
              options={[['auto', 'Auto'], ['rtl', 'RTL'], ['ltr', 'LTR']]}
              value={f.direction}
              onChange={(v) => set('direction', v)}
            />
          </div>
        </>
      )}

      {(section === 'all' || section === 'size') && (
        <Collapsible title="Spacing" defaultOpen={section === 'size'}>
          <SliderRow label="Letter spacing" value={f.letter_spacing} min={-20} max={100} step={0.5} onChange={(v) => set('letter_spacing', v)} format={(v) => `${v}px`} />
          <SliderRow label="Line height" value={f.line_spacing || 1.2} min={0.6} max={3} step={0.05} onChange={(v) => set('line_spacing', v)} format={(v) => v.toFixed(2)} />
          <SliderRow
            label="Max width"
            value={f.line_width || 90}
            min={10}
            max={90}
            onChange={(v) => set('line_width', v)}
            format={(v) => (f.line_width ? `${Math.round(v)}%` : 'No wrap')}
          />
          {f.line_width ? (
            <button type="button" onClick={() => set('line_width', null)} className={cn('self-start text-xs font-bold underline underline-offset-2 text-[#4A4640]', FOCUS_RING)}>
              Don&apos;t wrap lines
            </button>
          ) : null}
        </Collapsible>
      )}

      {show('effects') && (
        <div className="border-t border-[#EEEBE4] flex flex-col" data-testid="effects">
          <SwitchRow
            label="Outline"
            checked={f.stroke_width > 0}
            onChange={(on) => onPatch(on ? { stroke_width: 3, stroke_color: raw.stroke_color || '#FFFFFF' } : { stroke_width: 0 }, null)}
          >
            <ColorField label="Outline colour" value={f.stroke_color} compact swatches={[['White', '#FFFFFF'], ['Ink', '#1A1A1A'], ['Gold', '#B09536']]} onChange={(v) => set('stroke_color', v)} />
            <SliderRow label="Outline width" value={f.stroke_width} min={0.5} max={20} step={0.5} onChange={(v) => set('stroke_width', v)} format={(v) => `${v}px`} />
          </SwitchRow>
          <SwitchRow
            label="Shadow"
            checked={Boolean(f.shadow_color)}
            onChange={(on) => onPatch({ shadow_color: on ? 'rgba(26,26,26,0.35)' : null }, null)}
          >
            <ColorField label="Shadow colour" value={f.shadow_color} compact swatches={[['Soft black', 'rgba(26,26,26,0.35)'], ['Pink glow', '#EC008C'], ['Gold', '#B09536']]} onChange={(v) => set('shadow_color', v)} />
            <SliderRow label="Blur" value={f.shadow_blur} min={0} max={60} onChange={(v) => set('shadow_blur', v)} format={(v) => `${v}px`} />
            <SliderRow label="Offset across" value={f.shadow_x} min={-60} max={60} onChange={(v) => set('shadow_x', v)} format={(v) => `${v}px`} />
            <SliderRow label="Offset down" value={f.shadow_y} min={-60} max={60} onChange={(v) => set('shadow_y', v)} format={(v) => `${v}px`} />
          </SwitchRow>
          <SwitchRow
            label="Label background"
            checked={Boolean(f.bg_color)}
            onChange={(on) => onPatch({ bg_color: on ? '#FFC21A' : null }, null)}
          >
            <ColorField label="Label colour" value={f.bg_color} compact swatches={LABEL_SWATCHES} onChange={(v) => set('bg_color', v)} />
            <SliderRow label="Padding" value={f.bg_padding} min={0} max={60} onChange={(v) => set('bg_padding', v)} format={(v) => `${v}px`} />
            <SliderRow label="Corner radius" value={f.bg_radius} min={0} max={60} onChange={(v) => set('bg_radius', v)} format={(v) => `${v}px`} />
          </SwitchRow>
          <SwitchRow
            label="Highlight lines"
            checked={Boolean(f.highlight_color)}
            onChange={(on) => onPatch({ highlight_color: on ? '#F8C8E0' : null }, null)}
          >
            <ColorField label="Highlight colour" value={f.highlight_color} compact swatches={HIGHLIGHT_SWATCHES} onChange={(v) => set('highlight_color', v)} />
          </SwitchRow>
        </div>
      )}

      {glyphs && section === 'all' && (
        <Collapsible
          title={
            <span className="flex items-center gap-2" data-testid="open-glyphs">
              <Omega className="h-4 w-4" aria-hidden="true" />
              Glyphs &amp; decorative letters
            </span>
          }
        >
          {glyphs}
        </Collapsible>
      )}
      {glyphs && section === 'glyphs' && glyphs}

      {(section === 'all' || section === 'effects') && (
        <>
          <SliderRow
            label="Transparency"
            value={Math.round((f.opacity ?? 1) * 100)}
            min={0}
            max={100}
            onChange={(v) => set('opacity', v / 100)}
            format={(v) => `${v}%`}
            testId="opacity-slider"
          />
          <SliderRow label="Rotation" value={f.rotation} min={-180} max={180} onChange={(v) => set('rotation', v)} format={(v) => `${v}°`} />
        </>
      )}
    </div>
  );
};

/** Properties of a shape, line or photo. */
export const ElementProperties = ({ element: raw, onPatch }) => {
  const e = withElementDefaults(raw);
  const set = (prop, value) => onPatch({ [prop]: value }, `${e.id}-${prop}`);
  const isShape = e.type === 'rect' || e.type === 'ellipse';

  return (
    <div className="flex flex-col gap-3.5" data-testid="element-properties">
      <PanelHeader icon={Shapes} title={elementLabel(raw)} subtitle={e.type === 'image' ? 'Photo' : 'Element'} />

      {isShape && (
        <div className="border-t border-[#EEEBE4]">
          <SwitchRow label="Fill" checked={Boolean(e.fill)} onChange={(on) => onPatch({ fill: on ? '#B09536' : null }, null)}>
            <ColorField label="Fill colour" value={e.fill} swatches={COLOR_SWATCHES} onChange={(v) => set('fill', v)} />
          </SwitchRow>
          <SwitchRow
            label="Border"
            checked={e.stroke_width > 0}
            onChange={(on) => onPatch({ stroke_width: on ? 3 : 0 }, null)}
          >
            <ColorField label="Border colour" value={e.stroke_color} swatches={COLOR_SWATCHES} onChange={(v) => set('stroke_color', v)} />
            <SliderRow label="Border width" value={e.stroke_width} min={0.5} max={40} step={0.5} onChange={(v) => set('stroke_width', v)} format={(v) => `${v}px`} />
          </SwitchRow>
        </div>
      )}

      {e.type === 'line' && (
        <>
          <ColorField label="Line colour" value={e.stroke_color} swatches={COLOR_SWATCHES} onChange={(v) => set('stroke_color', v)} />
          <SliderRow label="Thickness" value={e.stroke_width} min={0.5} max={20} step={0.5} onChange={(v) => set('stroke_width', v)} format={(v) => `${v}px`} />
          <div className="flex flex-col gap-1.5">
            <span className={LABEL}>Line style</span>
            <Segmented
              label="Line style"
              options={[['solid', 'Solid'], ['dashed', 'Dashed'], ['dotted', 'Dotted']]}
              value={e.line_style}
              onChange={(v) => set('line_style', v)}
            />
          </div>
        </>
      )}

      {e.type === 'image' && (
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Fit</span>
          <Segmented
            label="Image fit"
            options={[['cover', 'Fill the box'], ['contain', 'Show whole photo']]}
            value={e.fit}
            onChange={(v) => set('fit', v)}
          />
        </div>
      )}

      {(e.type === 'rect' || e.type === 'image') && (
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Corners</span>
          <Segmented
            label="Corner style"
            options={[['round', 'Round'], ['bevel', 'Bevel'], ['scoop', 'Scoop'], ['notch', 'Notch']]}
            value={e.corner_style || 'round'}
            onChange={(v) => onPatch({ corner_style: v, ...(e.radius ? {} : { radius: 24 }) }, null)}
          />
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Corner size presets">
            {[['Square', 0], ['Small', 12], ['Medium', 32], ['Large', 72], ['Max', 1000]].map(([name, r]) => (
              <button
                key={name}
                type="button"
                onClick={() => set('radius', r)}
                className={cn('h-7 px-2.5 rounded-full border text-xs font-bold', e.radius === r ? 'bg-ink text-paper border-ink' : 'border-[#DCD6C8] hover:bg-cream', FOCUS_RING)}
              >
                {name}
              </button>
            ))}
          </div>
          <SliderRow label="Corner size" value={Math.min(e.radius, 300)} min={0} max={300} onChange={(v) => set('radius', v)} format={(v) => `${v}px`} />
        </div>
      )}

      {e.type === 'image' && (
        <Collapsible title="Adjust photo" defaultOpen>
          <div className="flex flex-col gap-2.5">
            <SliderRow label="Brightness" value={e.brightness ?? 1} min={0.3} max={1.7} step={0.02} onChange={(v) => set('brightness', v)} format={(v) => `${Math.round(v * 100)}%`} />
            <SliderRow label="Contrast" value={e.contrast ?? 1} min={0.3} max={1.7} step={0.02} onChange={(v) => set('contrast', v)} format={(v) => `${Math.round(v * 100)}%`} />
            <SliderRow label="Colour strength" value={e.saturation ?? 1} min={0} max={2} step={0.02} onChange={(v) => set('saturation', v)} format={(v) => (v === 0 ? 'Black & white' : `${Math.round(v * 100)}%`)} />
            <SliderRow label="Sepia (old photo)" value={e.sepia ?? 0} min={0} max={1} step={0.05} onChange={(v) => set('sepia', v)} format={(v) => `${Math.round(v * 100)}%`} />
            <SliderRow label="Blur" value={e.blur ?? 0} min={0} max={12} step={0.5} onChange={(v) => set('blur', v)} format={(v) => `${v}px`} />
            {e.fit !== 'contain' && (
              <>
                <SliderRow label="Show more left ↔ right" value={e.pos_x ?? 50} min={0} max={100} onChange={(v) => set('pos_x', v)} format={(v) => `${v}%`} />
                <SliderRow label="Show more top ↕ bottom" value={e.pos_y ?? 50} min={0} max={100} onChange={(v) => set('pos_y', v)} format={(v) => `${v}%`} />
              </>
            )}
            <div className="grid grid-cols-2 gap-2">
              {[['flip_h', 'Mirror ↔'], ['flip_v', 'Flip ↕']].map(([prop, name]) => (
                <button
                  key={prop}
                  type="button"
                  onClick={() => onPatch({ [prop]: !e[prop] }, null)}
                  aria-pressed={Boolean(e[prop])}
                  className={cn('h-9 rounded-full border text-[13px] font-bold', e[prop] ? 'bg-ink text-paper border-ink' : 'border-[#DCD6C8] hover:bg-cream', FOCUS_RING)}
                >
                  {name}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => onPatch({ brightness: 1, contrast: 1, saturation: 1, sepia: 0, blur: 0, flip_h: false, flip_v: false, pos_x: 50, pos_y: 50 }, null)}
              className={cn('h-8 rounded-full text-xs font-extrabold hover:bg-cream', FOCUS_RING)}
            >
              Reset photo adjustments
            </button>
          </div>
        </Collapsible>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className={LABEL}>Width %</span>
          <input
            type="number"
            min={0.5}
            max={200}
            step={0.5}
            value={Math.round(e.w * 10) / 10}
            onChange={(ev) => ev.target.value && set('w', Math.max(0.5, Number(ev.target.value)))}
            className={cn(TEXT_INPUT, 'h-10 text-sm tabular-nums')}
          />
        </label>
        {e.type !== 'line' && (
          <label className="flex flex-col gap-1">
            <span className={LABEL}>Height %</span>
            <input
              type="number"
              min={0.5}
              max={200}
              step={0.5}
              value={Math.round(e.h * 10) / 10}
              onChange={(ev) => ev.target.value && set('h', Math.max(0.5, Number(ev.target.value)))}
              className={cn(TEXT_INPUT, 'h-10 text-sm tabular-nums')}
            />
          </label>
        )}
      </div>

      <SliderRow
        label="Transparency"
        value={Math.round((e.opacity ?? 1) * 100)}
        min={0}
        max={100}
        onChange={(v) => set('opacity', v / 100)}
        format={(v) => `${v}%`}
      />
      <SliderRow label="Rotation" value={e.rotation} min={-180} max={180} onChange={(v) => set('rotation', v)} format={(v) => `${v}°`} />
    </div>
  );
};

/** Right panel when nothing is selected (the background was clicked): background effects + `children`. */
export const NothingSelected = ({ background, children }) => (
  <div className="flex flex-col gap-4">
    {background ? (
      <div className="flex flex-col gap-4" data-testid="background-properties">
        <PanelHeader icon={ImageIcon} title="Background" subtitle="Colour and effects for the design" />
        {background}
      </div>
    ) : null}
    <div className="rounded-[18px] bg-cream p-4 flex gap-3 items-center">
      <MousePointerClick className="h-5 w-5 shrink-0 text-[#6E5B1C]" aria-hidden="true" />
      <p className="text-[13px] leading-relaxed text-[#4A4640]">
        Select a line or element on the invitation to change its font, size, colour and effects.
      </p>
    </div>
    {children}
  </div>
);
